import { API_URL } from "env";
import { toast } from "react-toastify";

type NotificationData = {
    id: number;
    title: string;
    subtitle: string;
    message: string;
    is_read: boolean;
    createdAt: string;
    redirect_url: string | null;
}

function getUserID() {
    const data = localStorage.getItem('user');
    const user = data ? JSON.parse(data) : null;
    return user ? user.id : null;
}

async function getUnreadCount() {
    const userID = getUserID();
    if (!userID) return 0;
    try {
        const response = await fetch(`${API_URL}/api/v1/be/notifications/unread-count?user_id=${userID}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
        });
        if (response.ok) {
            const data = await response.json();
            return data.data.unread_count || 0;
        } else {
            console.error('Failed to fetch unread notifications count:', response.statusText);
            return 0;
        }
    }
    catch (error) {
        console.error('Error fetching unread notifications count:', error);
        return 0;
    }
}

async function markAsRead(notificationID: number) {
    try {
        const response = await fetch(`${API_URL}/api/v1/be/notifications/${notificationID}/mark-as-read`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
        });
        if (response.ok) {
            return true;
        } else {
            console.error('Failed to mark notification as read:', response.statusText);
            return false;
        }
    }
    catch (error) {
        console.error('Error marking notification as read:', error);
        return false;
    }
}

async function getNotificationPage(page: number, limit: number) {
    const userID = getUserID();
    if (!userID) return 0;
    try {
        const response = await fetch(`${API_URL}/api/v1/notifications?user_id=${userID}&page=${[page]}&limit=${limit}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
        });
        if (response.ok) {
            const data = await response.json();
            // Map API response to NotificationData[] and count unread notifications
            const notifications: NotificationData[] = data.data.data.map((item: any) => ({
                id: item.id,
                title: item.title,
                subtitle: item.subtitle,
                message: item.description,
                is_read: item.is_read,
                createdAt: item.created_at,
                redirect_url: item.redirect_url,
            }));
            return {
                data: notifications,
                count: data.data.total
            };
        } else {
            console.error('Failed to fetch unread notifications:', response.statusText);
            return 0;
        }
    }
    catch (error) {
        console.error('Error fetching unread notifications:', error);
        return 0;
    }
}

async function getNotificationPageUnread(page: number, limit: number, read?: boolean) {
    const userID = getUserID();
    if (!userID) return 0;
    try {
        const response = await fetch(`${API_URL}/api/v1/notifications?user_id=${userID}&is_read=${read !== undefined ? read === true ? 1 : 0 : 0}&page=${[page]}&limit=${limit}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
        });
        if (response.ok) {
            const data = await response.json();
            // Map API response to NotificationData[] and count unread notifications
            const notifications: NotificationData[] = data.data.data.map((item: any) => ({
                id: item.id,
                title: item.title,
                subtitle: item.subtitle,
                message: item.description,
                is_read: item.is_read,
                createdAt: item.created_at,
                redirect_url: item.redirect_url,
            }));
            console.log('count', data.data.total);
            return {
                data: notifications,
                count: data.data.total
            };
        } else {
            console.error('Failed to fetch unread notifications:', response.statusText);
            return 0;
        }
    }
    catch (error) {
        console.error('Error fetching unread notifications:', error);
        return 0;
    }
}

async function getNotifications() {
    const userID = getUserID();
    if (!userID) return 0;
    try {
        const response = await fetch(`${API_URL}/api/v1/notifications?user_id=${userID}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
        });
        if (response.ok) {
            const data = await response.json();
            // Map API response to NotificationData[] and count unread notifications
            const notifications: NotificationData[] = data.data.data.map((item: any) => ({
                id: item.id,
                title: item.title,
                subtitle: item.subtitle,
                message: item.description,
                is_read: item.is_read,
                createdAt: item.created_at,
                redirect_url: item.redirect_url,
            }));
            return {
                data: notifications,
                count: data.data.total
            };
        } else {
            console.error('Failed to fetch unread notifications:', response.statusText);
            return 0;
        }
    }
    catch (error) {
        console.error('Error fetching unread notifications:', error);
        return 0;
    }

}



async function getNotificationByID(notificationID: number) {
    try {
        const response = await fetch(`${API_URL}/api/v1/notifications/${notificationID}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
        });
        if (response.ok) {
            const data = await response.json();
            // Map API response to NotificationData
            const notification: NotificationData = {
                id: data.data.id,
                title: data.data.title,
                subtitle: data.data.subtitle,
                message: data.data.description,
                is_read: data.data.is_read,
                createdAt: data.data.created_at,
                redirect_url: data.data.redirect_url,
            };
            return notification;
        } else {
            console.error('Failed to fetch notification by ID:', response.statusText);
            return null;
        }
    }
    catch (error) {
        console.error('Error fetching notification by ID:', error);
        return null;
    }
}

async function markAllAsRead() {
    try {
        const user = localStorage.getItem('user');
        if (!user) {
            toast.error('User not logged in');
            return;
        }
        const uid = JSON.parse(user).id;
        if (!uid) {
            toast.error('User ID not found');
            return;
        }
        const res = await fetch(API_URL + `/api/v1/be/notifications/mark-all-as-read`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({ user_id: Number(uid) })
        });
        const data = await res.json();
        if (data.status) {
            toast.success('All notifications marked as read');
        } else {
            toast.error('Failed to mark all as read. Error: ' + (JSON.stringify(data.error) || 'Unknown error'));
        }
    }
    catch (error) {
        toast.error('Network error while marking all as read');
    }
}

export { getNotifications, getNotificationPage, getNotificationPageUnread, markAsRead, getUnreadCount, getNotificationByID, markAllAsRead };
export type { NotificationData };